import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, ArrowRight, X, CheckCircle2 } from "lucide-react";
import { useApp, API } from "../context";

const DISMISS_KEY = "iw_complete_profile_tip_dismissed_v1";

/**
 * CompleteProfileTip, a prominent, friendly first-screen reminder for
 * NEW account holders (parents, kids, AND solo explorers) that
 * completing their profile unlocks the full experience. Distinct from
 * `<ProfileCompletionNudge>` which is a percent-based banner for kid +
 * solo flows only, this tip is always-visible (until dismissed) for
 * every account type, designed for the "you just signed up, here's
 * what to do next" first-screen moment.
 *
 *   • Renders only when `profile_completion_percent < 100`.
 *   • Persistently dismissible via the X button (localStorage).
 *   • Reappears for the same user if they sign out + sign back in
 *     UNTIL they reach 100%.
 */
const CompleteProfileTip = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [pct, setPct] = useState(null);
  const [missing, setMissing] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Hydrate dismissal from localStorage (per-user key)
    try {
      const flag = localStorage.getItem(`${DISMISS_KEY}_${user.id}`);
      if (flag === "1") setDismissed(true);
    } catch (_) { /* SSR / private mode */ }
    // Pull canonical completion data from the unified profile service.
    axios.get(`${API}/profile/${user.id}`).then(r => {
      // Account-wide dismissal (set once, never nags again on any device).
      if (r.data?.onboarding_tip_dismissed) { setDismissed(true); return; }
      if (typeof r.data?.profile_completion_percent === "number") {
        setPct(r.data.profile_completion_percent);
      }
      if (Array.isArray(r.data?.profile_completion_missing)) {
        setMissing(r.data.profile_completion_missing);
      }
    }).catch(() => {});
  }, [user]);

  if (!user) return null;
  if (dismissed) return null;
  // Wait until we actually know the completion % before rendering, otherwise
  // the card flashes on every refresh for already-set-up users before the
  // profile API resolves (then vanishes once it loads).
  if (pct === null) return null;
  if (pct >= 100) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(`${DISMISS_KEY}_${user.id}`, "1"); } catch (_) {}
    // Persist account-wide so it never re-nags on re-login / another device.
    axios.post(`${API}/profile/${user.id}/dismiss-onboarding-tip`).catch(() => {});
  };

  const goToProfile = () => navigate("/profile");

  const headline = (() => {
    if (pct !== null && pct >= 75) return "You're almost there!";
    if (pct !== null && pct >= 40) return "Keep your profile rolling";
    return "Welcome! Let's set up your profile";
  })();

  return (
    <div
      className="relative rounded-2xl p-4 sm:p-5 border-2 overflow-hidden animate-fade-in"
      style={{
        background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(236,72,153,0.14), rgba(251,191,36,0.10))",
        borderColor: "rgba(196,181,253,0.4)",
        boxShadow: "0 0 32px rgba(139,92,246,0.18)",
      }}
      data-testid="complete-profile-tip"
    >
      {/* Soft animated sparkle backdrop */}
      <div className="absolute -top-6 -right-6 opacity-20 pointer-events-none">
        <Sparkles size={120} className="text-amber-300 animate-pulse" />
      </div>

      <button
        onClick={dismiss}
        className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/60 hover:text-white transition-colors"
        aria-label="Dismiss tip"
        data-testid="complete-profile-tip-close"
      >
        <X size={14} />
      </button>

      <div className="relative flex items-start gap-3 sm:gap-4 pr-8">
        <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #8B5CF6, #EC4899)" }}>
          <CheckCircle2 size={22} className="text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 text-[9px] font-bold tracking-wider uppercase rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/40">
              Tip · Recommended
            </span>
            {pct !== null && (
              <span className="text-xs text-white/50 font-bold" data-testid="complete-profile-tip-pct">
                {pct}% complete
              </span>
            )}
          </div>
          <h3 className="text-sm sm:text-base font-fredoka font-bold text-white mb-1" data-testid="complete-profile-tip-headline">
            {headline}
          </h3>
          <p className="text-xs sm:text-sm text-white/70 leading-relaxed mb-3">
            Completing your profile unlocks personalized recommendations, helps friends find you, and
            powers the leaderboards.
            {Array.isArray(missing) && missing.length > 0 && (
              <span className="block mt-1.5 text-white/55">
                <span className="text-white/70 font-bold">Still to add:</span>{" "}
                {missing.slice(0, 4).join(", ")}
                {missing.length > 4 && ` +${missing.length - 4} more`}
              </span>
            )}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={goToProfile}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.04]"
              style={{ background: "linear-gradient(135deg, #8B5CF6, #EC4899)", boxShadow: "0 4px 16px rgba(139,92,246,0.35)" }}
              data-testid="complete-profile-tip-cta"
            >
              Complete Profile
              <ArrowRight size={14} />
            </button>
            <button
              onClick={dismiss}
              className="text-xs text-white/40 hover:text-white/70 px-2 py-1 transition-colors"
              data-testid="complete-profile-tip-later"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfileTip;
